import type { RouteRecordRaw } from 'vue-router'

import { defineStore } from 'pinia'
import { type Ref, ref, watch } from 'vue'

import { http } from '@/extensions/http'
import i18n from '@/i18n'
import router from '@/router'

const initPages = async () => {
  const { data } = await http.get<Record<string, IndexPage>>('/pages')

  const defaultPages = new Set<PageTemplate>(['home', 'privacy', 'not_found'])
  const customPageKeys = new Set(['faq'])
  const locales: Locale[] = ['fr', 'en']

  Object.values(data)
    .filter(
      (page: IndexPage) =>
        !defaultPages.has(page.template) && !customPageKeys.has(page.key.toLowerCase()),
    )
    .reduce((acc: Array<RouteRecordRaw>, page: IndexPage) => {
      locales.forEach((pageLocale: Locale) => {
        acc.push({
          path: page.slugs[pageLocale],
          name: `${page.key}:${pageLocale}`,
          component: () => import('@/views/BuilderPage.vue'),
          meta: { locale: pageLocale },
        })
      })

      return acc
    }, [])
    .forEach((route: RouteRecordRaw) => {
      router.addRoute(route)
    })

  // Reset router to handle newly added route match
  await router.replace(router.currentRoute.value.fullPath)
}

export const useContentStore = defineStore('content', () => {
  const ready = ref<boolean>(false)
  const options = ref<Options>({})
  const currentLocale = i18n.global.locale

  const initOptions = async () => {
    const { data } = await http.get<Record<string, Option>>('/options')

    options.value = Object.values(data).reduce<Options>((acc, option) => {
      acc[option.key] = option.data
      return acc
    }, {})
  }

  const init = async () => {
    try {
      await Promise.all([initOptions(), initPages()])
      ready.value = true
    } catch (e) {
      // oxlint-disable-next-line no-console
      console.error(e)
    }
  }

  const pageMemory: Ref<Record<Locale, PageMemory>> = ref({
    en: {} as PageMemory,
    fr: {} as PageMemory,
  })

  const getPage = async (pageKey: string) => {
    const locale = currentLocale.value as Locale

    if (pageMemory.value[locale][pageKey] !== undefined) {
      return pageMemory.value[locale][pageKey]
    }

    const { data } = await http.get<ShowPage>(`/pages/${pageKey}`)

    pageMemory.value[locale][pageKey] = data

    return data
  }

  watch(currentLocale, async (to: Locale) => {
    await initOptions()

    const currentRoute = router.currentRoute.value
    const currentBaseName = currentRoute.name?.toString().split(':')[0] || ''
    const newRouteName = `${currentBaseName}:${to}`

    if (currentRoute.name !== newRouteName) {
      await router.replace({ name: newRouteName, params: currentRoute.params })
    }
  })

  return { options, ready, getPage, init }
})
